// The due WINDOWS — what each Dashboard tile counts, as a filter the list can
// apply (PATTERNS.md BP-93: "the count and the list are the same predicate").
//
//   tile                 dashboard → ../list?status=1&remaining=overdue
//   list (URL → seed)    useUrlFilterItems(DUE_DATE_URL_FILTERS)
//   grid → route         Status is Open + Remaining is Overdue
//   transformDueDateListParams  Remaining is <window> → `required` range specs
//
// The windows are GET /v6/duedate/summary's own definitions, over open rows:
//
//   overdue  required <  today
//   due_7    today ≤ required ≤ today + 7
//   due_30   today ≤ required ≤ today + 30
//   none     required IS NULL
//
// "today" here is the browser's local date (the Remaining column already counts
// from it, dueDateDates.ts); the summary's is the server's. They agree except
// across a midnight the two clocks disagree on.
import { addDays, format } from "date-fns";

/** The column whose filter carries the window — the grid's Remaining column. */
export const DUE_WINDOW_FIELD = "remaining";

/** Window → its label in the Filters panel (and the tile it came from). */
export const DUE_WINDOW_LABELS = {
  overdue: "Overdue",
  due_7: "Due in 7 days",
  due_30: "Due in 30 days",
  none: "No due date",
} as const;

export type DueWindow = keyof typeof DUE_WINDOW_LABELS;

export const isDueWindow = (value: unknown): value is DueWindow =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(DUE_WINDOW_LABELS, value);

/** The whiteboard status the summary's KPIs count (0 Closed, 1 Open, 2 Not Applicable). */
export const OPEN_STATUS = "1";

/**
 * The URL filters the list accepts from a Dashboard tile (BP-93). Field →
 * allowed values; module-level, because useUrlFilterItems memoises on it.
 */
export const DUE_DATE_URL_FILTERS = {
  status: ["0", "1", "2"],
  [DUE_WINDOW_FIELD]: Object.keys(DUE_WINDOW_LABELS),
} as const;

/** Filters-panel options for the Remaining column, one per window. */
export const DUE_WINDOW_OPTIONS = (Object.keys(DUE_WINDOW_LABELS) as DueWindow[]).map((value) => ({
  value,
  label: DUE_WINDOW_LABELS[value],
}));

export type RequiredSpec = { field: "required"; op: string; value: unknown };

const iso = (d: Date) => format(d, "yyyy-MM-dd");

/** A window as the generic route's filter specs on the real `required` column. */
export function dueWindowSpecs(window: DueWindow, today: Date = new Date()): RequiredSpec[] {
  const from = iso(today);
  switch (window) {
    case "overdue":
      return [{ field: "required", op: "<", value: from }];
    case "due_7":
      return [
        { field: "required", op: ">=", value: from },
        { field: "required", op: "<=", value: iso(addDays(today, 7)) },
      ];
    case "due_30":
      return [
        { field: "required", op: ">=", value: from },
        { field: "required", op: "<=", value: iso(addDays(today, 30)) },
      ];
    case "none":
      return [{ field: "required", op: "is_null", value: null }];
  }
}
